import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Tabs,
  Tab,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { Gear } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../context/BusinessContext';
import { useAuth } from '../../context/AuthContext';
import { useBusinessSubscription } from '../../hooks/useBusinessSubscription';
import DashboardCardManager from './DashboardCardManager';
import { CARD_REGISTRY, DEFAULT_ENABLED_CARDS } from './dashboardCards/cardRegistry';
import useThemeStyles from '../../theme/useThemeStyles';
import api from '../../utils/api';

const STORAGE_KEYS = {
  CARD_ORDER: 'supplierDashboardCardOrder',
  CARD_PREFERENCES: 'supplierDashboardCardPreferences',
};

const formatLocalYmd = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function SupplierDashboard() {
  const styles = useThemeStyles();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    selectedBusiness,
    loading: businessLoading,
    error: businessError,
    hasNoBusiness,
    hasPermission,
  } = useBusiness();
  const { isTrial, trialEndsAt } = useBusinessSubscription();

  const [cardOrder, setCardOrder] = useState([]);
  const [cardPreferences, setCardPreferences] = useState({});
  const [enabledCards, setEnabledCards] = useState([]);
  const [managerOpen, setManagerOpen] = useState(false);
  const [overviewScope, setOverviewScope] = useState('business');
  const [resolvedUserMatchIds, setResolvedUserMatchIds] = useState([]);
  const [selectedWeekStart, setSelectedWeekStart] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - date.getDay());
    date.setHours(0, 0, 0, 0);
    return date;
  });

  const _businessId = selectedBusiness?.business?._id;
  const userMatchIds = useMemo(() => {
    const ids = [
      user?._id,
      user?.id,
      selectedBusiness?._id,
      selectedBusiness?.member?._id,
      selectedBusiness?.member?.id,
      selectedBusiness?.businessMember?._id,
      selectedBusiness?.businessMember?.id,
      selectedBusiness?.staffMember?._id,
      selectedBusiness?.staffMember?.id,
    ].filter(Boolean).map((id) => String(id));
    return [...new Set(ids)];
  }, [selectedBusiness, user]);

  useEffect(() => {
    const businessId = selectedBusiness?.business?._id;
    const userId = user?._id || user?.id;
    if (!businessId || !userId) {
      setResolvedUserMatchIds(userMatchIds);
      return;
    }

    let active = true;
    const resolveIds = async () => {
      try {
        const [staffRes, membersRes] = await Promise.all([
          api.get(`/api/supplier/staff-members?businessId=${businessId}`).catch(() => ({ data: { staffMembers: [] } })),
          api.get(`/api/business-members/business/${businessId}`).catch(() => ({ data: { members: [] } })),
        ]);

        const currentUserId = String(userId);
        const staffMembers = Array.isArray(staffRes?.data?.staffMembers) ? staffRes.data.staffMembers : [];
        const businessMembers = Array.isArray(membersRes?.data?.members) ? membersRes.data.members : [];

        const matchedStaffIds = staffMembers
          .filter((staff) => String(staff?.user?._id || staff?.user?.id || '') === currentUserId)
          .flatMap((staff) => [staff?._id, staff?.id, staff?.businessMember?._id, staff?.businessMember?.id])
          .filter(Boolean)
          .map((id) => String(id));

        const matchedBusinessMemberIds = businessMembers
          .filter((member) => String(member?.user?._id || member?.user?.id || '') === currentUserId)
          .flatMap((member) => [member?._id, member?.id, member?.staffMember?._id, member?.staffMember?.id])
          .filter(Boolean)
          .map((id) => String(id));

        const mergedIds = [...new Set([...userMatchIds, ...matchedStaffIds, ...matchedBusinessMemberIds])];
        if (active) setResolvedUserMatchIds(mergedIds);
      } catch {
        if (active) setResolvedUserMatchIds(userMatchIds);
      }
    };

    void resolveIds();
    return () => {
      active = false;
    };
  }, [selectedBusiness?.business?._id, user?._id, user?.id, userMatchIds]);

  // Redirect to registration step 2 (Business Basics) if supplier has no business
  useEffect(() => {
    if (!businessLoading && hasNoBusiness) {
      navigate('/supplier/register');
    }
  }, [businessLoading, hasNoBusiness, navigate]);

  // Load preferences from localStorage on mount
  useEffect(() => {
    const savedOrder = localStorage.getItem(STORAGE_KEYS.CARD_ORDER);
    const savedPreferences = localStorage.getItem(STORAGE_KEYS.CARD_PREFERENCES);

    let preferences = {};
    if (savedPreferences) {
      try {
        preferences = JSON.parse(savedPreferences);
      } catch (e) {}
    }

    setCardPreferences(preferences);

    // Determine enabled cards
    const enabled = Object.keys(CARD_REGISTRY).filter(id => {
      if (preferences[id] !== undefined) {
        return preferences[id];
      }
      return DEFAULT_ENABLED_CARDS.includes(id);
    });

    // Apply saved order or use default
    let orderedCards = enabled;
    if (savedOrder) {
      try {
        const savedOrderArray = JSON.parse(savedOrder);
        // Merge saved order with enabled cards (in case new cards were added)
        const savedOrderSet = new Set(savedOrderArray);
        const newCards = enabled.filter(id => !savedOrderSet.has(id));
        orderedCards = [...savedOrderArray.filter(id => enabled.includes(id)), ...newCards];
      } catch (e) {}
    }

    setCardOrder(orderedCards);
    setEnabledCards(orderedCards);
  }, []);

  // Filter cards by permissions
  const getFilteredCards = useCallback(() => {
    return enabledCards
      .map(id => CARD_REGISTRY[id])
      .filter(card => {
        if (!card) return false;
        if (!card.permissions || card.permissions.length === 0) return true;
        return card.permissions.some(permission => hasPermission(permission));
      });
  }, [enabledCards, hasPermission]);

  const handleCardOrderChange = (newOrder) => {
    setCardOrder(newOrder);
    setEnabledCards(newOrder);
    localStorage.setItem(STORAGE_KEYS.CARD_ORDER, JSON.stringify(newOrder));
  };

  const handleToggleCard = (cardId) => {
    const isCurrentlyEnabled = enabledCards.includes(cardId);
    const newPreferences = {
      ...cardPreferences,
      [cardId]: !isCurrentlyEnabled,
    };
    setCardPreferences(newPreferences);
    localStorage.setItem(STORAGE_KEYS.CARD_PREFERENCES, JSON.stringify(newPreferences));

    // Update enabled cards
    const newEnabled = Object.keys(CARD_REGISTRY).filter(id => {
      if (newPreferences[id] !== undefined) {
        return newPreferences[id];
      }
      return DEFAULT_ENABLED_CARDS.includes(id);
    });

    // Maintain order: keep existing order for cards that remain enabled
    // If enabling a new card, add it to the end of enabled cards
    // If disabling, remove it but keep its position for when re-enabled
    const currentOrder = cardOrder.filter(id => newEnabled.includes(id));
    const newCards = newEnabled.filter(id => !currentOrder.includes(id));
    const finalOrder = [...currentOrder, ...newCards];

    setCardOrder(finalOrder);
    setEnabledCards(finalOrder);
    localStorage.setItem(STORAGE_KEYS.CARD_ORDER, JSON.stringify(finalOrder));
  };

  if (businessLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // If no business, redirect will happen via useEffect, but show loading while redirecting
  if (hasNoBusiness) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (businessError) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {businessError}
      </Alert>
    );
  }

  if (!selectedBusiness) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No businesses found. Please register a business first.
      </Alert>
    );
  }

  const filteredCards = getFilteredCards();

  const getTrialCountdown = () => {
    if (!isTrial || !trialEndsAt) return null;
    const end = new Date(trialEndsAt);
    const now = new Date();
    const msLeft = end - now;
    if (msLeft <= 0) return 'Trial ended';
    const hoursLeft = msLeft / (1000 * 60 * 60);
    const daysLeft = hoursLeft / 24;
    if (daysLeft >= 1) {
      const days = Math.round(daysLeft);
      return `${days} ${days === 1 ? 'day' : 'days'} left of trial`;
    }
    const hours = Math.floor(hoursLeft);
    const mins = Math.round((hoursLeft - hours) * 60);
    if (hoursLeft >= 12) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} left of trial`;
    }
    const parts = [];
    if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
    if (mins > 0 || hours === 0) parts.push(`${mins} ${mins === 1 ? 'min' : 'mins'}`);
    return `${parts.join(' ')} left of trial`;
  };

  const trialCountdown = getTrialCountdown();
  const weekOptions = Array.from({ length: 17 }, (_, index) => {
    const weekStart = new Date(selectedWeekStart);
    weekStart.setDate(weekStart.getDate() + (index - 8) * 7);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    return {
      value: formatLocalYmd(weekStart),
      label: `${weekStart.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' })}`,
      weekStart,
    };
  });

  const handleWeekChange = (value) => {
    const selectedWeek = weekOptions.find((option) => option.value === value);
    if (!selectedWeek) return;
    setSelectedWeekStart(selectedWeek.weekStart);
  };

  const dashboardContext = {
    overviewScope,
    weekStartMs: selectedWeekStart.getTime(),
    userMatchIds: resolvedUserMatchIds.length > 0 ? resolvedUserMatchIds : userMatchIds,
  };

  return (
    <Box>
      {/* Welcome Header */}
      <Box sx={{ 
        mb: { xs: 3, sm: 3, md: 4 }, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'flex-start' }, 
        gap: { xs: 2, sm: 2 },
        color: 'rgba(0, 0, 0, 1)' 
      }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
            <Typography component="h2" className="pagename" sx={styles.text.pagename}>
              Welcome back, {selectedBusiness?.business.name || 'your business'}
            </Typography>
            {trialCountdown && (
              <Chip
                label={trialCountdown}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 400,
              mb: { xs: 1, sm: 2 }, 
              color: 'text.secondary',
              fontSize: '1rem',
              lineHeight: 1.5
            }}
          >
            Your bookings, couples, and messages at a glance
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          mb: { xs: 2, sm: 3 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Tabs
          value={overviewScope}
          onChange={(_, nextScope) => {
            if (!nextScope) return;
            setOverviewScope(nextScope);
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 42,
            '& .MuiTab-root': {
              minHeight: 42,
              textTransform: 'none',
              fontWeight: 500,
            },
          }}
        >
          <Tab value="business" label="Business Overview" />
          <Tab value="my" label="My Overview" />
        </Tabs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => setManagerOpen(true)}
            aria-label="Choose what appears on your dashboard"
            sx={{ border: '1px solid', borderColor: 'divider' }}
          >
            <Gear size={20} weight="regular" />
          </IconButton>
          <FormControl size="small" sx={{ minWidth: 210 }}>
            <Select
              value={formatLocalYmd(selectedWeekStart)}
              onChange={(event) => handleWeekChange(event.target.value)}
            >
              {weekOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Dashboard Grid */}
      {filteredCards.length === 0 ? (
        <Alert severity="info">
          You haven’t picked anything for your dashboard yet. Use the gear icon to choose what you see.
        </Alert>
      ) : (
        <Grid container spacing={{ xs: 2, sm: 2, md: 3 }}>
          {filteredCards.map((card) => {
            const CardComponent = card.component;
            // Custom column widths for specific cards - fully responsive
            let gridSize = { xs: 12, sm: 6, md: 4, lg: 3 }; // Default: responsive columns
            let gridItemSx = {};
            if (card.id === 'upcoming-appointments') {
              gridSize = { xs: 12, sm: 12, md: 8, lg: 6 }; // 2 columns wide on desktop
              gridItemSx = {
                '@media (min-width:1200px)': {
                  flexBasis: '40%',
                  maxWidth: '40%',
                },
              };
            } else if (card.id === 'pending-bookings' || card.id === 'unread-messages') {
              gridSize = { xs: 12, sm: 12, md: 6, lg: 3 };
              gridItemSx = {
                '@media (min-width:1200px)': {
                  flexBasis: '30%',
                  maxWidth: '30%',
                },
              };
            } else if (card.id === 'staff-schedule') {
              gridSize = { xs: 12, sm: 12, md: 12, lg: 9 }; // Full width on tablet, 3 columns on desktop
            }
            
            return (
              <Grid item xs={gridSize.xs} sm={gridSize.sm} md={gridSize.md} lg={gridSize.lg} key={card.id} sx={gridItemSx}>
                <CardComponent dashboardContext={dashboardContext} />
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Card Manager Dialog */}
      <DashboardCardManager
        open={managerOpen}
        onClose={() => setManagerOpen(false)}
        enabledCards={enabledCards}
        cardOrder={cardOrder}
        onToggleCard={handleToggleCard}
        onCardOrderChange={handleCardOrderChange}
      />
    </Box>
  );
}

export default SupplierDashboard;
